'use client';

import Link from 'next/link';
import { useEffect, useSyncExternalStore } from 'react';
import { Heart, LogOut, Settings, User } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useFavoritesStore } from '@/stores/favoritesStore';
import { hasSession } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';

const emptySubscribe = () => () => {};

/**
 * Auth island. Bootstraps auth app-wide — but only downloads the Firebase SDK
 * when a previous session flag exists (or the user heads to /login).
 */
export default function AuthMenu() {
  // Hydration-safe client detection without a setState-in-effect
  const mounted = useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false
  );
  const { user, isInitialized, logout, initAuth } = useAuthStore();
  const { loadFavorites, clearFavorites, completePendingFavorite } = useFavoritesStore();

  useEffect(() => {
    return initAuth();
  }, [initAuth]);

  useEffect(() => {
    if (user) {
      // Load the user's favorites, then resume any auth-gated favorite a guest
      // started before signing in (so the heart they tapped ends up saved).
      loadFavorites().then(completePendingFavorite);
    } else {
      clearFavorites();
    }
  }, [user, loadFavorites, clearFavorites, completePendingFavorite]);

  // Restoring a known session — hold layout steady with a placeholder
  if (mounted && !isInitialized && hasSession()) {
    return (
      <AuthSlot>
        <Skeleton className="h-9 w-24 rounded-xl" />
      </AuthSlot>
    );
  }

  if (mounted && user) {
    return (
      <AuthSlot>
        <Link
          href="/favorites"
          className="rounded-lg p-2 text-ink-muted transition-colors hover:bg-glass hover:text-emerald-300"
          title="Favorites"
        >
          <Heart className="h-4.5 w-4.5" />
        </Link>
        <Link
          href="/dashboard"
          className="flex items-center gap-2 rounded-lg bg-glass px-3 py-1.5 transition-colors hover:bg-glass-bright"
        >
          <User className="h-4 w-4 shrink-0 text-emerald-300" />
          {/* Truncated so the slot has a ceiling as well as a floor — a long
              first name must not be able to widen the header. */}
          <span className="max-w-[7rem] truncate text-sm font-medium text-ink">
            {user.first_name || 'Account'}
          </span>
        </Link>
        <Link
          href="/settings"
          className="rounded-lg p-2 text-ink-muted transition-colors hover:bg-glass hover:text-ink"
          title="Settings"
        >
          <Settings className="h-4.5 w-4.5" />
        </Link>
        <button
          onClick={logout}
          className="rounded-lg p-2 text-ink-muted transition-colors hover:bg-glass hover:text-ink"
          title="Log out"
        >
          <LogOut className="h-4.5 w-4.5" />
        </button>
      </AuthSlot>
    );
  }

  return (
    <AuthSlot gap="gap-2">
      <Link
        href="/login"
        className="px-3 py-2 text-sm font-medium text-ink-soft transition-colors hover:text-ink"
      >
        Log in
      </Link>
      <Button size="sm" asChild>
        <Link href="/signup">Get started</Link>
      </Button>
    </AuthSlot>
  );
}

/**
 * A fixed box for whichever of the three auth states is showing.
 *
 * The states are three different widths — measured at 1280: signed out **171 px**
 * ("Log in" + "Get started"), the session-restoring placeholder **96 px**, signed
 * in **220 px** — and the server always ships the signed-out one, because it has
 * no way to know. So a returning buyer's header went 171 → 96 → 220 px, dragging
 * the nav links sideways twice: two layout shifts totalling **CLS 0.0046** on
 * every page, measured on the home page while signing in. (There was no shift for
 * a guest, which is why it had never shown up.)
 *
 * Reserving the widest state and right-aligning inside it holds the box still.
 * Guests are unaffected visually — their content was already flush right.
 * (W14 R133b)
 */
function AuthSlot({ children, gap = 'gap-1.5' }: { children: React.ReactNode; gap?: string }) {
  return (
    <div className={`flex h-9 w-[220px] items-center justify-end ${gap}`}>{children}</div>
  );
}
